import { supabase } from '@/integrations/supabase/client';

interface ImportResult {
  imported: number;
  duplicates: number;
  matched: number;
  unmatchedCount: number;
  totalParsed: number;
  invalidRows: number;
}

function parseCSV(csvContent: string): { rows: Array<{ Name: string; Email: string }>; invalidCount: number } {
  const lines = csvContent.trim().split(/\r?\n/);
  if (lines.length < 2) return { rows: [], invalidCount: 0 };

  const headerLine = lines[0];
  // Support both comma and tab delimiters
  const delimiter = headerLine.includes('\t') ? '\t' : ',';
  const headers = headerLine.split(delimiter).map(h => h.trim().replace(/^"|"$/g, ''));

  const nameIdx = headers.findIndex(h => h.toLowerCase() === 'name');
  const emailIdx = headers.findIndex(h => h.toLowerCase() === 'email');

  if (nameIdx === -1 || emailIdx === -1) {
    throw new Error('CSV must have "Name" and "Email" columns');
  }

  const rows: Array<{ Name: string; Email: string }> = [];
  let invalidCount = 0;
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const cols = line.split(delimiter).map(c => c.trim().replace(/^"|"$/g, ''));
    const name = cols[nameIdx]?.trim() || '';
    const email = cols[emailIdx]?.trim().toLowerCase() || '';

    if (email && email.includes('@')) {
      rows.push({ Name: name, Email: email });
    } else {
      invalidCount++;
    }
  }
  return { rows, invalidCount };
}

export async function importRecipientsFromCSV(
  campaignId: string,
  csvContent: string,
  companyId: string
): Promise<ImportResult> {
  const { rows, invalidCount: invalidRows } = parseCSV(csvContent);
  if (rows.length === 0) throw new Error('No valid rows found in CSV');
  const totalParsed = rows.length + invalidRows;

  // Fetch campaign for sender defaults
  const { data: campaign, error: campErr } = await supabase
    .from('email_campaigns')
    .select('subject, sender_config')
    .eq('id', campaignId)
    .single();
  if (campErr || !campaign) throw new Error('Campaign not found');

  const senderConfig = (campaign.sender_config || {}) as Record<string, string>;
  const senderEmail = senderConfig.from_email || senderConfig.sender_email || 'noreply@example.com';
  const senderName = senderConfig.from_name || senderConfig.sender_name || '';
  const subject = campaign.subject || '';

  // Fetch existing queue emails to skip duplicates
  const { data: existingItems } = await supabase
    .from('email_queue')
    .select('recipient_email')
    .eq('campaign_id', campaignId);

  const existingEmails = new Set(
    (existingItems || []).map(i => i.recipient_email.toLowerCase())
  );

  let imported = 0;
  let duplicates = 0;

  // Batch insert in chunks of 50 — all with lead_id: null (matched server-side after)
  const toInsert: Array<{
    campaign_id: string;
    lead_id: null;
    recipient_email: string;
    recipient_name: string;
    sender_email: string;
    sender_name: string;
    subject: string;
    status: string;
  }> = [];

  for (const row of rows) {
    if (existingEmails.has(row.Email)) {
      duplicates++;
      continue;
    }
    existingEmails.add(row.Email);

    toInsert.push({
      campaign_id: campaignId,
      lead_id: null,
      recipient_email: row.Email,
      recipient_name: row.Name,
      sender_email: senderEmail,
      sender_name: senderName,
      subject,
      status: 'pending',
    });
  }

  // Insert in batches
  for (let i = 0; i < toInsert.length; i += 50) {
    const batch = toInsert.slice(i, i + 50);
    const { error } = await supabase.from('email_queue').insert(batch);
    if (error) throw new Error(`Insert failed: ${error.message}`);
    imported += batch.length;
  }

  // Match leads server-side in one query (scales to any number of leads)
  const { data: matchedCount } = await supabase.rpc('match_queue_leads', {
    p_campaign_id: campaignId,
    p_company_id: companyId,
  });

  // Get unmatched count
  const { count: unmatchedCount } = await supabase
    .from('email_queue')
    .select('*', { count: 'exact', head: true })
    .eq('campaign_id', campaignId)
    .is('lead_id', null);

  // Update campaign recipient_filters and total_recipients
  const { count } = await supabase
    .from('email_queue')
    .select('*', { count: 'exact', head: true })
    .eq('campaign_id', campaignId);

  await supabase
    .from('email_campaigns')
    .update({
      recipient_filters: { target: 'from_queue' },
      total_recipients: count ?? 0,
    })
    .eq('id', campaignId);

  return { imported, duplicates, matched: matchedCount ?? 0, unmatchedCount: unmatchedCount ?? 0, totalParsed, invalidRows };
}
